<template>
	<ClientOnly>
		<div
			v-if="ad"
			ref="adRoot"
			class="author-side-box mb-[32px]"
		>
			<div
				class="author-watchlist-subscription-ad"
				style="text-align: left !important"
			>
				<div
					v-if="imageUrl"
					class="author-watchlist-image"
				>
				<a
					:href="trackingLink"
					target="_blank"
					rel="noopener noreferrer"
					@click.passive="onAdClick"
				>
					<img
						width="340"
							height="224"
							:src="imageUrl"
							:alt="header"
							class="author-post-image-image"
							loading="lazy"
						/>
					</a>
				</div>

				<div class="author-watchlist-information">
					<a
						:href="trackingLink"
						target="_blank"
						rel="noopener noreferrer"
					>
						<h4
							class="author-watchlist-information-title"
							style="text-align: left !important"
						>
							{{ header }}
						</h4>
					</a>
					<span
						v-if="description"
						class="author-watchlist-information-subtitle"
						style="text-align: left !important"
					>
						{{ description }}
					</span>
					<a
						:href="trackingLink"
						class="author-watchlist-information-subscribe-button"
						style="text-align: left !important"
						target="_blank"
						rel="noopener noreferrer"
					>
						{{ cta }}
					</a>
				</div>
			</div>
		</div>
	</ClientOnly>
</template>

<script setup>
const props = defineProps({
	slotId: {
		type: String,
		default: "c26b2507-1c06-43a3-bf84-32efaf76127e"
	}
});

const AIZEN_BASE_URL = "https://aizen-ad-manager.up.railway.app";

const ad = useState(`aizen-ad-${props.slotId}`, () => null);
const adLoading = useState(`aizen-ad-loading-${props.slotId}`, () => false);
const impressionTracked = useState(
	`aizen-ad-impression-${props.slotId}`,
	() => false
);

const adRoot = ref(null);

const normalizeAizenUrl = (url) => {
	if (!url) {
		return null;
	}
	if (url.startsWith("http://") || url.startsWith("https://")) {
		return url;
	}
	return `${AIZEN_BASE_URL}${url}`;
};

const { pageKey } = usePageKey()
const adCtx = computed(() => ({ pageKey: pageKey.value, surface: "sidebar" }))
const trackingLink = computed(() =>
	buildAdClickUrl(
		normalizeAizenUrl(ad.value?.clickUrl) || ad.value?.destinationUrl || "#",
		adCtx.value
	)
);
const onAdClick = () => fireAdClickEvent(adCtx.value)

const imageUrl = computed(() => ad.value?.imageUrl || "");

const header = computed(() => {
	const properties = ad.value?.properties || {};
	return properties.title || ad.value?.name || "";
});

const description = computed(() => {
	const properties = ad.value?.properties || {};
	return properties.body || "";
});

const cta = computed(() => {
	const properties = ad.value?.properties || {};
	return (
		properties.cta_label || properties.cta || properties.CTA || "Learn More"
	);
});

useAizenImpression(
	adRoot,
	() => normalizeAizenUrl(ad.value?.impressionUrl),
	{ firedRef: impressionTracked }
);

const fetchAd = async () => {
	if (ad.value || adLoading.value) {
		return;
	}

	adLoading.value = true;
	try {
		const response = await $fetch(
			`${AIZEN_BASE_URL}/api/slots/${props.slotId}/serve`
		);

		if (response?.clickUrl || response?.destinationUrl) {
			ad.value = response;
		}
	} catch {
	} finally {
		adLoading.value = false;
	}
};

const scheduleFetch = () => {
	const load = () => {
		fetchAd();
	};

	if (typeof window.requestIdleCallback === "function") {
		window.requestIdleCallback(load, { timeout: 2000 });
		return;
	}

	window.setTimeout(load, 0);
};

onMounted(() => {
	if (process.client) {
		scheduleFetch();
	}
});
</script>

<style lang="scss" scoped>
@import "@assets/utils.scss";

.author-side-box {
	width: 100%;
	border-radius: 24px;
	background-color: #fff;
	display: flex;
	flex-direction: column;
	padding: 20px;
	gap: 20px;
	box-shadow: 0 7px 20px 0 rgba(0, 0, 0, 0.10196078431372549);

	@include breakpoint(md) {
		width: 100%;
		max-width: 380px;
	}
}

.author-watchlist-subscription-ad {
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.author-watchlist-image {
	width: 100%;
	overflow: hidden;
	border-radius: 12px;
}

.author-watchlist-image img {
	width: 100%;
	height: auto;
	object-fit: cover;
	border-radius: 4px;
}

.author-watchlist-information-title {
	padding-top: 24px;
	padding-bottom: 0;
	font-size: 24px;
	font-weight: 700;
	line-height: 29px;
	letter-spacing: -0.5px;
	text-align: left;

	@include breakpoint(lg) {
		font-size: 20px;
	}

	@include breakpoint(xxl) {
		font-size: 26px;
	}
}

.author-watchlist-information-subtitle {
	padding-top: 8px;
	font-size: 16px;
	font-weight: 400;
	line-height: 24px;
	letter-spacing: 0;
	color: #1e293b;
	width: 100%;
	display: block;
	margin-bottom: 12px;
}

.author-watchlist-information-subscribe-button {
	background-color: #ef6820;
	color: #fff;
	font-weight: 700;
	border: none;
	border-radius: 8px;
	padding: 10px 12px;
	font-size: 16px;
	cursor: pointer;
	transition: background-color 0.3s ease;
	width: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 12px;
	text-decoration: none;

	&:hover {
		background-color: #d65a16;
	}
}
</style>
